import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Card, CardContent, IconButton, Tooltip, Button } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getEndpoint, API_ENDPOINTS } from '../api/apiConstants';
import { api } from '../api/fetchWrapper';
import { createStepSetting, updateStepSetting, deleteStepSetting } from '../api/features/jobDetail/jobDetailApi';
import { SAPButton } from '../components/design-system/SAPButton';
import { SAPTextField } from '../components/design-system/SAPTextField';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS, SAP_TRANSITIONS } from '../theme/sapDesignSystem';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { toast } from 'sonner';

interface Props {
  stepId: number;
}

type StepSettingRow = { id: number; stepId: number; value: string; key: string; clientId?: number };

export default function StepSettings({ stepId }: Props) {
  const { clientCode } = useClientCode();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<StepSettingRow[]>([]);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState<StepSettingRow | null>(null);
  const [inlineEditingId, setInlineEditingId] = React.useState<number | null>(null);
  const [inlineValue, setInlineValue] = React.useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteConfirmRow, setDeleteConfirmRow] = React.useState<StepSettingRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!stepId) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.STEP_SETTINGS);
      const url = `${endpoint}?firstResult=-1&maxResult=0&stepId=${stepId}`;
      const data = await api.get<any>(url);
      const arr: any[] = Array.isArray(data) ? data : (Array.isArray((data as any)?.rows) ? (data as any).rows : []);
      const normalized: StepSettingRow[] = arr.map((r: any) => Array.isArray(r)
        ? { id: r[0], stepId: r[1], value: r[2], key: r[3], clientId: r[4] }
        : { id: r.id, stepId: r.stepId, value: r.value, key: r.key, clientId: r.clientId }
      );
      setRows(normalized);
    } catch (e: any) {
      setError(e?.message || 'Failed to load step settings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientCode, stepId]);

  React.useEffect(() => { load(); }, [load]);

  const startInlineEdit = (row: StepSettingRow) => {
    setInlineEditingId(row.id);
    setInlineValue(row.value || '');
  };

  const cancelInlineEdit = () => {
    setInlineEditingId(null);
    setInlineValue('');
  };

  const saveInlineEdit = async (row: StepSettingRow) => {
    try {
      const payload = {
        id: row.id,
        stepId: row.stepId,
        key: row.key,
        value: inlineValue,
        clientId: row.clientId || 0,
      };
      await updateStepSetting({ clientCode, settingData: payload });
      toast.success('Step setting updated');
      await load();
      cancelInlineEdit();
    } catch (e: any) {
      toast.error(e?.message || 'Update failed');
    }
  };

  const openDeleteConfirm = (row: StepSettingRow) => {
    setDeleteConfirmRow(row);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmRow) return;
    setDeleting(true);
    try {
      await deleteStepSetting({ clientCode, id: deleteConfirmRow.id });
      toast.success('Step setting deleted');
      await load();
      setDeleteConfirmOpen(false);
      setDeleteConfirmRow(null);
      if (inlineEditingId === deleteConfirmRow.id) cancelInlineEdit();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header with actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {/* Removed redundant "Step Settings" heading since it's already shown in tabs */}
          
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Add Step Setting">
              <span>
                <IconButton 
                  onClick={() => { setEditRow({ id: 0, stepId, key: '', value: '', clientId: undefined }); setEditOpen(true); }} 
                  disabled={loading} 
                  sx={{ 
                    color: SAP_COLORS.sapBlue,
                    backgroundColor: SAP_COLORS.sapLightBlue,
                    borderRadius: SAP_SPACING.component.borderRadius,
                    boxShadow: SAP_SHADOWS.sm,
                    '&:hover': {
                      backgroundColor: SAP_COLORS.sapBlue,
                      color: SAP_COLORS.sapWhite,
                      transform: 'translateY(-2px)',
                      boxShadow: SAP_SHADOWS.md,
                    },
                    '&:disabled': {
                      backgroundColor: SAP_COLORS.sapGray2,
                      color: SAP_COLORS.sapGray4,
                      boxShadow: 'none',
                      transform: 'none',
                    },
                    transition: SAP_TRANSITIONS.fast,
                  }} 
                  size="large"
                >
                  <AddIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '2fr 1fr auto' }, alignItems: 'center', px: 2, py: 1.25, backgroundColor: SAP_COLORS.sapGray1, borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>Key</Typography>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium, display: { xs: 'none', sm: 'block' } }}>Value</Typography>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium, display: { xs: 'none', sm: 'block' } }}>Actions</Typography>
          </Box>

          {loading && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">Loading settings...</Typography>
            </Box>
          )}
          {error && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="error.main">{error}</Typography>
            </Box>
          )}
          {!loading && !error && rows.length === 0 && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">No settings found.</Typography>
            </Box>
          )}

          {rows.map((s) => (
            <Box key={s.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr auto' }, alignItems: 'center', px: 2, py: 1.25, borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
              <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.key ?? '-'}</Typography>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {inlineEditingId === s.id ? (
                  <SAPTextField size="sm" value={inlineValue} onChange={(e) => setInlineValue(e.target.value)} />
                ) : (
                  <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.value ?? '-'}</Typography>
                )}
              </Box>
              <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                {inlineEditingId === s.id ? (
                  <>
                    <Tooltip title="Save"><IconButton onClick={() => saveInlineEdit(s)} sx={{ color: SAP_COLORS.sapGreen }}><SaveIcon /></IconButton></Tooltip>
                    <Tooltip title="Cancel"><IconButton onClick={cancelInlineEdit} sx={{ color: SAP_COLORS.sapGray5 }}><CloseIcon /></IconButton></Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Edit"><IconButton onClick={() => startInlineEdit(s)} sx={{ color: SAP_COLORS.sapBlue }}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton onClick={() => openDeleteConfirm(s)} sx={{ color: SAP_COLORS.sapRed || '#d32f2f' }}><DeleteIcon /></IconButton></Tooltip>
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>

      {/* Fallback dialog editor if needed later */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Step Setting</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>key</Typography>
              <SAPTextField value={editRow?.key ?? ''} onChange={(e) => setEditRow(r => ({ ...(r as StepSettingRow), key: e.target.value }))} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>value</Typography>
              <SAPTextField value={editRow?.value ?? ''} onChange={(e) => setEditRow(r => ({ ...(r as StepSettingRow), value: e.target.value }))} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <SAPButton variant="ghost" onClick={() => setEditOpen(false)}>Cancel</SAPButton>
          <SAPButton variant="primary" icon="save" onClick={async () => {
            try {
              if (!editRow) return;
              if (!editRow.id) {
                const settingData = { 
                  stepId, 
                  key: editRow.key, 
                  value: editRow.value, 
                  clientId: editRow.clientId || 0 
                };
                await createStepSetting({ clientCode, settingData });
                toast.success('Step setting created');
              } else {
                const settingData = { 
                  id: editRow.id, 
                  stepId, 
                  key: editRow.key, 
                  value: editRow.value, 
                  clientId: editRow.clientId || 0 
                };
                await updateStepSetting({ clientCode, settingData });
                toast.success('Step setting updated');
              }
              setEditOpen(false);
              await load();
            } catch (e: any) {
              toast.error(e?.message || 'Save failed');
            }
          }}>Save</SAPButton>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && (setDeleteConfirmOpen(false), setDeleteConfirmRow(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>Delete step setting?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteConfirmRow ? (
              <>Are you sure you want to delete the setting &quot;{deleteConfirmRow.key}&quot;? This action cannot be undone.</>
            ) : (
              'Are you sure you want to delete this step setting?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmRow(null); }} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}